import { AfterViewInit, Directive, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { CellClickEvent } from '@progress/kendo-angular-grid';
import _ from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';
import { buffer, debounceTime, filter, map } from 'rxjs/operators';

@Directive({
   selector: 'kendo-grid:[cellDoubleClick]',
})
export class CellDoubleClickDirective implements OnDestroy, AfterViewInit {
   @Output()
   public cellDoubleClick: EventEmitter<CellClickEvent> = new EventEmitter<CellClickEvent>();

   @Input()
   public maxDoubleClickTime = 250;

   private cellClickEvents$ = new BehaviorSubject<CellClickEvent>(null);
   private subscription: Subscription;

   ngOnDestroy(): void {
      this.subscription?.unsubscribe();
   }

   @HostListener('cellClick', ['$event'])
   public onCellClick(cellClickEvent: CellClickEvent): void {
      this.cellClickEvents$.next(cellClickEvent);
   }

   ngAfterViewInit(): void {
      // Kendo grid does not yet natively provide a "cell double clicked" event.
      // Therefore we create our own, based on cell click events.
      // See also: https://feedback.telerik.com/kendo-angular-ui/1609610-add-double-click-event
      this.subscription = this.cellClickEvents$
         .pipe(
            buffer(this.cellClickEvents$.pipe(debounceTime(this.maxDoubleClickTime))),
            filter((cellClickEvents) => cellClickEvents.length >= 2),
            // get last two clicks
            map((cellClickEvents) => _.takeRight(cellClickEvents, 2)),
            // only if the same cell was clicked.
            filter(([firstClick, secondClick]) => firstClick.rowIndex === secondClick.rowIndex && firstClick.columnIndex === secondClick.columnIndex),
            // Use the first event to trigger; this way we trigger the intended (first clicked) cell in case the second click
            // would happen on the same cell but (e.g. due to data being reloaded) the underlying data has changed.
            // Otherwise we would have to check the dataItem for equality in a generic way.
            map(([firstClick, secondClick]) => firstClick)
         )
         .subscribe((doubleClickEvent) => this.cellDoubleClick.emit(doubleClickEvent));
   }
}
